// src/pages/Calendario.jsx - V2 DATACONTEXT MIGRATION
// ======================================================
// 🔄 MIGRADO: database.js → DataContext (DualSyncStrategy V14)
// ✅ Online/Offline automático — Tombstones V14, Anti-duplicados V8
// ✅ Zero breaking changes — Misma UI, misma lógica de negocio
// 🏢 V3 — Portal Enterprise Integration (modales)
// ======================================================
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Plus,
  Trash2,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  MapPin,
  Edit3,
  Phone,
  Users,
  ListChecks,
  AlertCircle,
  X,
  Loader2,
  Eye,
  CheckCircle2,
} from "lucide-react";
import { useAuth } from "../contexts/useAuth";
import { useNotifications } from "../contexts/NotificationsContext";
import { useIsMobile } from "../hooks/useMediaQuery";
import DataContext from "../lib/data/context/DataContext"; // 🆕 V2: DataContext
import { useEscapeKey } from "../utils/portalUtils";
import NativeSheet from "../components/ui/NativeSheet";

// ============================================================================
// 🎯 CONSTANTES E CONFIGURAÇÕES
// ============================================================================
const DEFAULT_COLOR = "#3b82f6";

const EVENT_TYPES = {
  visit: { label: 'Visita', icon: MapPin, color: '#3b82f6' },
  meeting: { label: 'Reunião', icon: Users, color: '#8b5cf6' },
  call: { label: 'Ligação', icon: Phone, color: '#10b981' },
  task: { label: 'Tarefa', icon: ListChecks, color: '#f59e0b' },
  other: { label: 'Outro', icon: CalendarIcon, color: '#64748b' }
};

// ============================================================================
// 🎯 COMPONENTE EVENT CARD (MEMOIZADO PARA PERFORMANCE)
// ============================================================================
const EventCard = React.memo(({ event, client, property, onEdit, onDelete, onDragStart, onDragEnd, isMobile }) => {
  const eventColor = event.color || DEFAULT_COLOR;
  
  return (
    <div
      draggable={!isMobile}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        e.stopPropagation();
        onEdit(event);
      }}
      title={!isMobile ? "Arraste para outro dia ou clique para editar" : "Clique para editar"}
      className={`px-2 py-1 rounded-lg text-white shadow-sm cursor-pointer flex justify-between items-start group transition-all ${
        isMobile ? 'hover:shadow-md' : 'hover:shadow-md'
      }`}
      style={{
        backgroundColor: eventColor,
        opacity: 0.95,
      }}
    >
      <div className="flex-1 flex flex-col gap-[2px] min-w-0">
        <div className="flex items-center gap-1 flex-wrap">
          {event.timeStart && (
            <span className="inline-flex items-center gap-1 text-[10px] text-white/90">
              <Clock size={10} />
              {event.timeStart}
            </span>
          )}
          <span className="font-semibold truncate text-[11px]">
            {event.title}
          </span>
        </div>

        <div className="flex flex-col gap-[1px]">
          {client && (
            <span className="inline-flex items-center gap-1 text-[10px] text-white/90 truncate">
              <User size={10} />
              {client.name}
            </span>
          )}
          {property && (
            <span className="inline-flex items-center gap-1 text-[10px] text-white/90 truncate">
              <MapPin size={10} />
              {property.title || property.name}
            </span>
          )}
        </div>
      </div>

      <div className={`flex items-center gap-1 ml-1 ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(event);
          }}
          className="text-white/90 hover:text-white p-1 rounded hover:bg-card/20 transition-colors"
          title="Editar evento"
        >
          <Edit3 size={12} />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(event.id);
          }}
          className="text-white/80 hover:text-red-100 p-1 rounded hover:bg-card/20 transition-colors"
          title="Excluir evento"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
});

EventCard.displayName = 'EventCard';

// ============================================================================
// 🎯 COMPONENTE CALENDAR DAY (MEMOIZADO)
// ============================================================================
const CalendarDay = React.memo(({ 
  day, 
  events, 
  clients, 
  properties, 
  isToday, 
  isDragTarget,
  onNewEvent,
  onEventDrop,
  onEditEvent,
  onDeleteEvent,
  onDragStart,
  onDragEnd,
  draggedEvent,
  isMobile
}) => {
  const { dayNumber, dateKey } = day;

  const handleDragOver = useCallback((e) => {
    if (!draggedEvent || isMobile) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, [draggedEvent, isMobile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (!draggedEvent || draggedEvent.date === dateKey || isMobile) return;
    onEventDrop(draggedEvent.id, dateKey);
  }, [draggedEvent, dateKey, onEventDrop, isMobile]);

  const baseCellClasses = `
    relative rounded-xl border border-color/70 bg-card/90 backdrop-blur-sm 
    p-2 flex flex-col gap-1 min-h-[110px] hover:shadow-md hover:-translate-y-[1px] 
    transition-all group cursor-pointer
  `;

  const todayClasses = `
    border-blue-500/80 ring-1 ring-blue-500/40 
    bg-gradient-to-br from-blue-50 to-slate-50 shadow-sm
  `;

  const dragTargetClasses = `
    ring-2 ring-dashed ring-blue-400/80 bg-blue-50/60
  `;

  const dayClasses = [
    baseCellClasses,
    isToday ? todayClasses : "",
    (isDragTarget && !isMobile) ? dragTargetClasses : "",
  ].join(" ");

  return (
    <button
      type="button"
      onClick={() => onNewEvent(dateKey)}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={dayClasses}
    >
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-1">
          {isToday ? (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-semibold">
              {dayNumber}
            </span>
          ) : (
            <span className="text-sm font-semibold text-secondary">
              {dayNumber}
            </span>
          )}
        </div>

        <span className={`text-blue-500 text-[11px] inline-flex items-center gap-1 bg-blue-50/80 px-2 py-0.5 rounded-full border border-blue-100 shadow-sm transition-opacity ${
          isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}>
          <Plus size={11} />
          Novo
        </span>
      </div>

      <div className="flex flex-col gap-1 mt-1 overflow-y-auto max-h-[80px] scrollbar-thin scrollbar-thumb-slate-200">
        {events.length === 0 ? (
          <span className="text-[10px] text-slate-300 italic">
            Nenhum evento
          </span>
        ) : (
          events.map((event) => {
            const client = clients.find(c => c.id === event.clientId);
            const property = properties.find(p => p.id === event.propertyId);
            
            return (
              <EventCard
                key={event.id}
                event={event}
                client={client}
                property={property}
                onEdit={onEditEvent}
                onDelete={onDeleteEvent}
                onDragStart={(e) => onDragStart(e, event)}
                onDragEnd={onDragEnd}
                isMobile={isMobile}
              />
            );
          })
        )}
      </div>
    </button>
  );
});

CalendarDay.displayName = 'CalendarDay';

// ============================================================================
// 🎯 CONFIRM — NativeSheet (bottom sheet móvil / dialog desktop)
// ============================================================================
const ConfirmModal = ({ isOpen, data, onConfirm, onCancel, isMobile }) => {
  if (!data) return null;

  const footer = (
    <div className={`flex gap-2 ${isMobile ? "flex-col-reverse" : "justify-end"}`}>
      {!data.onlyOk && (
        <button
          type="button"
          onClick={onCancel}
          className={`rounded-xl font-medium border border-color text-secondary hover:bg-hover transition active:scale-[0.98] ${
            isMobile ? "w-full py-3 text-sm" : "px-4 py-2.5 text-sm"
          }`}
        >
          Cancelar
        </button>
      )}
      <button
        type="button"
        onClick={onConfirm}
        className={`rounded-xl font-semibold text-white shadow-md transition active:scale-[0.98] ${
          data.onlyOk ? "bg-blue-600 hover:bg-blue-500" : "bg-red-600 hover:bg-red-500"
        } ${isMobile ? "w-full py-3 text-sm" : "px-4 py-2.5 text-sm"}`}
      >
        {data.onlyOk ? "OK" : "Confirmar"}
      </button>
    </div>
  );

  return (
    <NativeSheet
      open={!!isOpen}
      onClose={onCancel}
      title={data.onlyOk ? "Atenção" : "Confirmar"}
      tone={data.onlyOk ? "default" : "danger"}
      footer={footer}
    >
      <p className={`text-secondary leading-relaxed ${isMobile ? "text-sm" : "text-sm md:text-base"}`}>
        {data.message}
      </p>
    </NativeSheet>
  );
};

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL - CALENDÁRIO V2 (DATACONTEXT)
// ============================================================================
export default function Calendario() {
  const { user } = useAuth(); // 🆕 V2: user en vez de database
  const { showToast, addNotification } = useNotifications();
  const calendarRef = useRef(null);
  const isMobile = useIsMobile();
  
  // 🆕 V2: DataContext instance (lazy init, singleton)
  const dataContextRef = useRef(null);
  const [ctxReady, setCtxReady] = useState(false);

  // 🎨 WHITE LABEL - Cores personalizadas
  const primaryColor = useMemo(() => {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--primary-color').trim() || DEFAULT_COLOR;
  }, []);

  // 🔄 STATES
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [viewMode, setViewMode] = useState(isMobile ? 'list' : 'month');

  const [form, setForm] = useState({
    id: null,
    title: "",
    description: "",
    date: "",
    timeStart: "",
    timeEnd: "",
    clientId: "",
    propertyId: "",
    color: DEFAULT_COLOR,
    type: "other",
  });

  // 🏢 V4: NativeSheet controla portal/scroll; confirm usa NativeSheet
  useEscapeKey(showModal, () => {
    if (formHasChanges()) {
      setConfirmData({
        message: "Você tem alterações não salvas. Deseja realmente sair?",
        onlyOk: false,
        onConfirm: () => {
          setShowModal(false);
          resetForm();
          setConfirmData(null);
        },
      });
    } else {
      setShowModal(false);
      resetForm();
    }
  });

  // ==========================================================================
  // 🆕 V2: INICIALIZAR DATACONTEXT (SINGLETON, UNA SOLA VEZ)
  // ==========================================================================
  useEffect(() => {
    let cancelled = false;
    
    const initContext = async () => {
      if (!user?.uid) return;
      
      try {
        const ctx = await DataContext.getInstance(user.uid);
        if (!cancelled) {
          dataContextRef.current = ctx;
          setCtxReady(true);
          console.log('✅ [Calendario V2] DataContext inicializado — userId:', user.uid);
        }
      } catch (error) {
        console.error('❌ [Calendario V2] Error inicializando DataContext:', error);
        if (!cancelled) {
          showToast("Erro ao conectar com o banco de dados", "error", {
            title: "Erro de conexão"
          });
        }
      }
    };

    initContext();
    return () => { cancelled = true; };
  }, [user?.uid, showToast]);

  // ==========================================================================
  // 📦 CARGA INICIAL DE DADOS (V2 — DataContext)
  // ==========================================================================
  useEffect(() => {
    if (!ctxReady || !dataContextRef.current) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const ctx = dataContextRef.current;

        const [eventsData, clientsData, propertiesData] = await Promise.all([
          ctx.collections.calendarEvents.read(),
          ctx.collections.clients.read(),
          ctx.collections.properties.read()
        ]);

        setEvents(Array.isArray(eventsData) ? eventsData : []);
        setClients(Array.isArray(clientsData) ? clientsData : []);
        setProperties(Array.isArray(propertiesData) ? propertiesData : []);

      } catch (error) {
        console.error('❌ [Calendario V2] Error cargando datos:', error);
        showToast("Não foi possível carregar os dados do calendário", "error", {
          title: "Erro ao carregar"
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [ctxReady, showToast]);

  // ==========================================================================
  // ⌨️ ATALHOS DE TECLADO (solo desktop)
  // ==========================================================================
  useEffect(() => {
    if (isMobile) return;
    
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        openModalForNew();
      }
      if (!e.ctrlKey && !e.shiftKey) {
        if (e.key === 'ArrowLeft') goToPrevMonth();
        if (e.key === 'ArrowRight') goToNextMonth();
      }
      if (e.key === 't' && !e.ctrlKey) {
        e.preventDefault();
        goToToday();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile]);

  // ==========================================================================
  // 🧮 HELPERS DE FECHAS
  // ==========================================================================
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const daysOfWeek = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

  const monthName = useMemo(() => 
    currentDate.toLocaleDateString("pt-BR", {
      month: "long",
      year: "numeric",
    }), [currentDate]
  );

  const getDaysInMonth = useCallback((y, m) => 
    new Date(y, m + 1, 0).getDate(), []
  );

  const getStartWeekdayIndex = useCallback((date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return (day + 6) % 7;
  }, []);

  const daysInMonth = getDaysInMonth(year, month);
  const startIndex = getStartWeekdayIndex(currentDate);

  // ==========================================================================
  // 📅 MATRIZ DE DIAS DO CALENDÁRIO (MEMOIZADA)
  // ==========================================================================
  const calendarDays = useMemo(() => {
    const totalCells = Math.ceil((startIndex + daysInMonth) / 7) * 7;
    const cells = [];

    for (let cellIndex = 0; cellIndex < totalCells; cellIndex++) {
      const dayNumber = cellIndex - startIndex + 1;
      if (dayNumber < 1 || dayNumber > daysInMonth) {
        cells.push(null);
      } else {
        const dayDate = new Date(year, month, dayNumber);
        const yyyy = dayDate.getFullYear();
        const mm = String(dayDate.getMonth() + 1).padStart(2, "0");
        const dd = String(dayDate.getDate()).padStart(2, "0");
        const dateKey = `${yyyy}-${mm}-${dd}`;
        cells.push({ dayNumber, dateKey, dateObj: dayDate });
      }
    }
    return cells;
  }, [year, month, daysInMonth, startIndex]);

  // ==========================================================================
  // 📌 MAPA DE EVENTOS POR DIA (COM VALIDAÇÕES)
  // ==========================================================================
  const validEvents = useMemo(() => {
    return events.filter(e => e?.date && e.date.match(/^\d{4}-\d{2}-\d{2}$/));
  }, [events]);

  const eventsByDate = useMemo(() => {
    const map = {};
    validEvents.forEach((event) => {
      const dateKey = event.date;
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(event);
    });

    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => {
        const tA = a.timeStart || "00:00";
        const tB = b.timeStart || "00:00";
        return tA.localeCompare(tB);
      });
    });

    return map;
  }, [validEvents]);

  const todayKey = useMemo(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  // ==========================================================================
  // 📋 PRÓXIMOS EVENTOS (MEMOIZADO)
  // ==========================================================================
  const upcomingEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return validEvents
      .map(e => {
        const [y, m, d] = e.date.split("-");
        const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
        return { ...e, _dateObj: dateObj };
      })
      .filter(e => e._dateObj >= today)
      .sort((a, b) => a._dateObj - b._dateObj)
      .slice(0, 10);
  }, [validEvents]);

  // ==========================================================================
  // 🔄 HANDLERS DO FORMULÁRIO
  // ==========================================================================
  const resetForm = useCallback(() => {
    setForm({
      id: null,
      title: "",
      description: "",
      date: "",
      timeStart: "",
      timeEnd: "",
      clientId: "",
      propertyId: "",
      color: DEFAULT_COLOR,
      type: "other",
    });
  }, []);

  const openModalForNew = useCallback((dateKey = "") => {
    setForm({
      id: null,
      title: "",
      description: "",
      date: dateKey || "",
      timeStart: "",
      timeEnd: "",
      clientId: "",
      propertyId: "",
      color: DEFAULT_COLOR,
      type: "other",
    });
    setShowModal(true);
  }, []);

  const openModalForEdit = useCallback((event) => {
    setForm({
      id: event.id ?? null,
      title: event.title || "",
      description: event.description || "",
      date: event.date || "",
      timeStart: event.timeStart || "",
      timeEnd: event.timeEnd || "",
      clientId: event.clientId || "",
      propertyId: event.propertyId || "",
      color: event.color || DEFAULT_COLOR,
      type: event.type || "other",
    });
    setShowModal(true);
  }, []);

  const formHasChanges = useCallback(() => {
    if (!form.id) {
      return (
        form.title ||
        form.description ||
        form.date ||
        form.timeStart ||
        form.timeEnd ||
        form.clientId ||
        form.propertyId ||
        form.color !== DEFAULT_COLOR ||
        form.type !== "other"
      );
    }
    return true;
  }, [form]);

  // ==========================================================================
  // 💾 HANDLE SAVE (V2 — DataContext)
  // ==========================================================================
  const validateForm = useCallback(() => {
    if (!form.title?.trim()) {
      setConfirmData({
        message: "O título do evento é obrigatório.",
        onlyOk: true,
      });
      return false;
    }

    if (!form.date) {
      setConfirmData({
        message: "A data do evento é obrigatória.",
        onlyOk: true,
      });
      return false;
    }

    if (form.timeStart && form.timeEnd && form.timeEnd < form.timeStart) {
      setConfirmData({
        message: "A hora de término é menor que a de início. Deseja continuar?",
        onlyOk: false,
        onConfirm: () => executeSave(),
      });
      return false;
    }

    return true;
  }, [form]);

  // 🆕 V2: Refrescar eventos desde DataContext
  const refreshEvents = useCallback(async () => {
    try {
      const ctx = dataContextRef.current;
      if (!ctx) return;
      const eventsData = await ctx.collections.calendarEvents.read();
      setEvents(Array.isArray(eventsData) ? eventsData : []);
    } catch (error) {
      console.error('❌ [Calendario V2] Error refrescando eventos:', error);
    }
  }, []);

  const executeSave = useCallback(async () => {
    const ctx = dataContextRef.current;
    if (!ctx) {
      showToast("Erro de conexão com o banco de dados", "error", {
        title: "Erro ao salvar"
      });
      return;
    }

    const payload = {
      title: form.title.trim(),
      description: form.description?.trim() || "",
      date: form.date,
      timeStart: form.timeStart || null,
      timeEnd: form.timeEnd || null,
      clientId: form.clientId || null,
      propertyId: form.propertyId || null,
      color: form.color || DEFAULT_COLOR,
      type: form.type || "other",
    };

    try {
      if (form.id) {
        await ctx.collections.calendarEvents.update(form.id, payload);
        showToast(`"${payload.title}" foi atualizado.`, "success", {
          title: "Evento atualizado"
        });
        addNotification({
          type: "info",
          title: "Evento atualizado",
          message: `"${payload.title}" foi atualizado.`,
          metadata: {
            eventId: form.id,
            date: payload.date
          }
        });
      } else {
        await ctx.collections.calendarEvents.create(payload);
        showToast(`"${payload.title}" foi adicionado.`, "success", {
          title: "Evento criado"
        });
        addNotification({
          type: "success",
          title: "Evento criado",
          message: `"${payload.title}" foi adicionado.`,
          metadata: {
            date: payload.date
          }
        });
      }

      await refreshEvents();
      setShowModal(false);
      resetForm();
      setConfirmData(null);
    } catch (error) {
      console.error('❌ [Calendario V2] Error al guardar:', error);
      showToast(error.message || "Erro ao salvar", "error", {
        title: "Erro ao salvar"
      });
    }
  }, [form, showToast, addNotification, resetForm, refreshEvents]);

  const handleSave = useCallback(() => {
    if (validateForm()) {
      if (form.id) {
        setConfirmData({
          message: "Deseja salvar as alterações neste evento?",
          onlyOk: false,
          onConfirm: executeSave,
        });
      } else {
        executeSave();
      }
    }
  }, [validateForm, form.id, executeSave]);

  // ==========================================================================
  // 🗑️ HANDLE DELETE (V2 — DataContext)
  // ==========================================================================
  const handleDelete = useCallback((id) => {
    setConfirmData({
      message: "Deseja realmente excluir este evento?",
      onlyOk: false,
      onConfirm: async () => {
        const ctx = dataContextRef.current;
        if (!ctx) {
          showToast("Erro de conexão com o banco de dados", "error", {
            title: "Erro ao excluir"
          });
          return;
        }

        try {
          const eventToDelete = events.find(e => e.id === id);
          await ctx.collections.calendarEvents.delete(id);
          await refreshEvents();
          setConfirmData(null);
          
          showToast("O evento foi removido com sucesso.", "success", {
            title: "Evento excluído"
          });
          
          if (eventToDelete) {
            addNotification({
              type: "info",
              title: "Evento excluído",
              message: `"${eventToDelete.title}" foi removido.`,
              metadata: {
                eventId: id
              }
            });
          }
        } catch (error) {
          console.error('❌ [Calendario V2] Error al eliminar:', error);
          showToast(error.message || "Erro ao excluir", "error", {
            title: "Erro ao excluir"
          });
        }
      },
    });
  }, [events, showToast, addNotification, refreshEvents]);

  // ==========================================================================
  // 🧲 DRAG & DROP HANDLERS (solo desktop — V2 DataContext)
  // ==========================================================================
  const handleDragStart = useCallback((e, event) => {
    if (isMobile) return;
    e.dataTransfer.effectAllowed = "move";
    setDraggedEvent({ id: event.id, date: event.date });
  }, [isMobile]);

  const handleDragEnd = useCallback(() => {
    setDraggedEvent(null);
  }, []);

  const handleEventDrop = useCallback(async (eventId, targetDate) => {
    if (isMobile) return;
    
    const ctx = dataContextRef.current;
    if (!ctx) return;

    const event = events.find(e => e.id === eventId);
    if (!event || event.date === targetDate) return;

    try {
      await ctx.collections.calendarEvents.update(eventId, { date: targetDate });
      await refreshEvents();
      
      showToast(`"${event.title}" foi movido com sucesso.`, "success", {
        title: "Evento movido"
      });
      
      addNotification({
        type: "info",
        title: "Evento movido",
        message: `"${event.title}" foi movido para ${new Date(targetDate).toLocaleDateString('pt-BR')}`,
        metadata: {
          eventId,
          oldDate: event.date,
          newDate: targetDate
        }
      });
    } catch (error) {
      console.error('❌ [Calendario V2] Error al mover:', error);
      showToast(error.message || "Erro ao mover", "error", {
        title: "Erro ao mover"
      });
    }
    setDraggedEvent(null);
  }, [events, showToast, addNotification, isMobile, refreshEvents]);

  // ==========================================================================
  // 📆 NAVEGAÇÃO
  // ==========================================================================
  const goToPrevMonth = useCallback(() => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }, []);

  const goToNextMonth = useCallback(() => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }, []);

  const goToToday = useCallback(() => {
    const today = new Date();
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
    if (isMobile) setViewMode('list');
  }, [isMobile]);

  // ==========================================================================
  // 🎨 LOADING SKELETON
  // ==========================================================================
  if (loading) {
    return (
      <div className={isMobile ? "p-3 space-y-3.5 pb-8" : "p-4 md:p-6 space-y-6"}>
        <div className="flex justify-between items-center">
          <div className={`${isMobile ? 'h-8 w-32' : 'h-10 w-48'} bg-muted rounded-xl animate-pulse`}></div>
          <div className={`${isMobile ? 'h-8 w-24' : 'h-10 w-32'} bg-muted rounded-xl animate-pulse`}></div>
        </div>
        {!isMobile && (
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded-xl animate-pulse"></div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ==========================================================================
  // 🎨 RENDERIZAÇÃO PRINCIPAL
  // ==========================================================================
  return (
    <div className={isMobile ? "p-3 space-y-3.5 pb-8" : "p-4 md:p-6 space-y-6"} ref={calendarRef}>
      {/* Estilos para animações */}
      <style>{`
        @keyframes calendarSlideIn {
          from { opacity: 0; transform: translateX(8px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .calendar-slide-in {
          animation: calendarSlideIn 0.25s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.25s ease-out; }
      `}</style>

      {/* ======================================== */}
      {/* HEADER */}
      {/* ======================================== */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-2">
          <div 
            className={`rounded-2xl text-white shadow-md flex items-center justify-center ${
              isMobile ? 'p-2' : 'p-3'
            }`}
            style={{ backgroundColor: primaryColor }}
          >
            <CalendarIcon size={isMobile ? 18 : 22} />
          </div>
          <div>
            <h1 className={`font-semibold text-primary ${isMobile ? 'text-lg' : 'text-xl md:text-2xl'}`}>
              Calendário
            </h1>
            {!isMobile && (
              <p className="text-xs text-muted mt-1">
                Organize visitas, reuniões e compromissos
                <span className="hidden md:inline"> • Atalhos: Ctrl+N (novo), T (hoje), ← → (meses)</span>
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {/* Toggle de visualização (mobile) */}
          {isMobile && (
            <button
              onClick={() => setViewMode(prev => prev === 'month' ? 'list' : 'month')}
              className="px-3 py-2 surface-card rounded-xl text-xs flex items-center gap-2 flex-1 justify-center"
            >
              {viewMode === 'month' ? <ListChecks size={14} /> : <CalendarIcon size={14} />}
              {viewMode === 'month' ? 'Lista' : 'Mês'}
            </button>
          )}

          {/* Navegação */}
          <div className="flex items-center gap-1 surface-card rounded-full px-1 py-1 shadow-sm">
            <button
              onClick={goToPrevMonth}
              className={`rounded-full hover:bg-muted text-secondary transition-colors flex items-center justify-center ${
                isMobile ? 'w-7 h-7' : 'w-8 h-8'
              }`}
              title="Mês anterior (←)"
            >
              <ChevronLeft size={isMobile ? 14 : 16} />
            </button>
            <button
              onClick={goToToday}
              className={`px-2 font-medium rounded-full hover:bg-muted text-secondary ${
                isMobile ? 'text-[10px] py-1' : 'text-xs py-1'
              }`}
              title="Ir para hoje (T)"
            >
              Hoje
            </button>
            <button
              onClick={goToNextMonth}
              className={`rounded-full hover:bg-muted text-secondary transition-colors flex items-center justify-center ${
                isMobile ? 'w-7 h-7' : 'w-8 h-8'
              }`}
              title="Próximo mês (→)"
            >
              <ChevronRight size={isMobile ? 14 : 16} />
            </button>
          </div>

          <button
            onClick={() => openModalForNew()}
            className={`inline-flex items-center gap-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 shadow-md transition-all active:scale-[0.98] ${
              isMobile ? 'px-3 py-2 text-xs flex-1 justify-center' : 'px-4 py-2 text-sm'
            }`}
            style={{ backgroundColor: primaryColor }}
            title={!isMobile ? "Novo evento (Ctrl+N)" : "Novo evento"}
          >
            <Plus size={isMobile ? 14 : 16} />
            <span>{isMobile ? 'Novo' : 'Novo Evento'}</span>
          </button>
        </div>
      </div>

      {/* ======================================== */}
      {/* MÊS ATUAL */}
      {/* ======================================== */}
      <div className="flex items-center justify-between">
        <h2 className={`font-semibold capitalize text-primary ${isMobile ? 'text-base' : 'text-lg'}`}>
          {monthName}
        </h2>
        <span className={`px-3 py-1 rounded-full surface-card text-muted ${
          isMobile ? 'text-[10px]' : 'text-xs'
        }`}>
          {isMobile ? 'Toque nos dias' : 'Arraste eventos para mudar o dia'}
        </span>
      </div>

      {/* ======================================== */}
      {/* VISÃO MENSAL / LISTA */}
      {/* ======================================== */}
      {(!isMobile || viewMode === 'month') ? (
        <>
          {/* Cabeçalho días de la semana */}
          <div className="grid grid-cols-7 gap-2 text-[10px] font-medium text-muted border-b border-color pb-2">
            {daysOfWeek.map((d) => (
              <div key={d} className="text-center uppercase tracking-[0.08em]">
                {isMobile ? d.substring(0,3) : d}
              </div>
            ))}
          </div>

          {/* Grade del calendario */}
          <div
            key={`${year}-${month}`}
            className="grid grid-cols-7 gap-2 calendar-slide-in"
          >
            {calendarDays.map((cell, index) => {
              if (!cell) {
                return <div key={index} className="min-h-[90px]" />;
              }

              const { dayNumber, dateKey } = cell;
              const dayEvents = eventsByDate[dateKey] || [];
              const isToday = dateKey === todayKey;
              const isDragTarget = draggedEvent && draggedEvent.date !== dateKey;

              return (
                <CalendarDay
                  key={dateKey}
                  day={cell}
                  events={dayEvents}
                  clients={clients}
                  properties={properties}
                  isToday={isToday}
                  isDragTarget={isDragTarget}
                  onNewEvent={openModalForNew}
                  onEventDrop={handleEventDrop}
                  onEditEvent={openModalForEdit}
                  onDeleteEvent={handleDelete}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  draggedEvent={draggedEvent}
                  isMobile={isMobile}
                />
              );
            })}
          </div>
        </>
      ) : (
        /* Visión en lista para móvil */
        <div className="space-y-3">
          {upcomingEvents.length === 0 ? (
            <p className="text-sm text-muted text-center py-8">
              Nenhum evento futuro cadastrado.
            </p>
          ) : (
            upcomingEvents.map((event) => {
              const client = clients.find(c => c.id === event.clientId);
              const property = properties.find(p => p.id === event.propertyId);
              const eventColor = event.color || primaryColor;
              const EventIcon = EVENT_TYPES[event.type]?.icon || CalendarIcon;

              return (
                <div
                  key={event.id}
                  className="surface-card rounded-xl p-3 shadow-sm hover:shadow-md transition-all cursor-pointer"
                  onClick={() => openModalForEdit(event)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 flex-1">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: eventColor }}
                      >
                        <EventIcon size={14} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="font-medium text-primary text-sm">
                            {event.title}
                          </span>
                          {event.timeStart && (
                            <span className="text-[10px] text-muted flex items-center gap-0.5">
                              <Clock size={10} />
                              {event.timeStart}
                            </span>
                          )}
                        </div>

                        <p className="text-[10px] text-muted mt-0.5">
                          {new Date(event.date).toLocaleDateString('pt-BR')}
                        </p>

                        {(client || property) && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {client && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] bg-muted px-1.5 py-0.5 rounded">
                                <User size={8} />
                                {client.name}
                              </span>
                            )}
                            {property && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] bg-muted px-1.5 py-0.5 rounded">
                                <MapPin size={8} />
                                {property.title || property.name}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openModalForEdit(event);
                        }}
                        className="p-1.5 hover:bg-muted rounded-lg transition-colors"
                      >
                        <Edit3 size={14} className="text-blue-600" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(event.id);
                        }}
                        className="p-1.5 hover:bg-muted rounded-lg transition-colors"
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ======================================== */}
      {/* PRÓXIMOS EVENTOS (DESKTOP) */}
      {/* ======================================== */}
      {!isMobile && (
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-primary">
              Próximos eventos
            </h3>
            <span className="text-[11px] text-muted">
              {upcomingEvents.length} evento(s) futuro(s)
            </span>
          </div>

          {upcomingEvents.length === 0 ? (
            <p className="text-xs text-muted">Nenhum evento futuro cadastrado.</p>
          ) : (
            <div className="space-y-2">
              {upcomingEvents.map((event) => {
                const client = clients.find(c => c.id === event.clientId);
                const property = properties.find(p => p.id === event.propertyId);
                const eventColor = event.color || primaryColor;
                const EventIcon = EVENT_TYPES[event.type]?.icon || CalendarIcon;

                return (
                  <div
                    key={event.id}
                    className="flex justify-between items-center surface-card rounded-xl px-4 py-3 text-xs shadow-sm hover:shadow-md hover:-translate-y-[1px] transition-all cursor-pointer"
                    onClick={() => openModalForEdit(event)}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-2 h-2 rounded-full"
                          style={{ backgroundColor: eventColor }}
                        />
                        <EventIcon size={12} className="text-muted" />
                        <span className="font-semibold text-primary">
                          {event.title}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-muted">
                        <span className="inline-flex items-center gap-1">
                          <Clock size={11} />
                          {new Date(event.date).toLocaleDateString('pt-BR')}
                          {event.timeStart && ` • ${event.timeStart}`}
                        </span>
                        {client && (
                          <span className="inline-flex items-center gap-1">
                            <User size={11} />
                            {client.name}
                          </span>
                        )}
                        {property && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin size={11} />
                            {property.title || property.name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openModalForEdit(event);
                        }}
                        className="text-blue-600 hover:text-blue-800 p-1"
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(event.id);
                        }}
                        className="text-red-500 hover:text-red-600 p-1"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ======================================== */}
      
      {/* ======================================== */}
      {/* 🏢 V4: MODAL EVENTO — NativeSheet */}
      {/* ======================================== */}
      <NativeSheet
        open={showModal}
        onClose={() => {
          if (formHasChanges()) {
            setConfirmData({
              message: "Você tem alterações não salvas. Deseja realmente sair?",
              onlyOk: false,
              onConfirm: () => {
                setShowModal(false);
                resetForm();
                setConfirmData(null);
              },
            });
          } else {
            setShowModal(false);
            resetForm();
          }
        }}
        title={form.id ? "Editar Evento" : "Novo Evento"}
        maxWidth="max-w-md"
        footer={
          <div className={`flex gap-2 ${isMobile ? "flex-col-reverse" : "justify-end"}`}>
            <button
              type="button"
              onClick={() => {
                if (formHasChanges()) {
                  setConfirmData({
                    message: "Você tem alterações não salvas. Deseja realmente sair?",
                    onlyOk: false,
                    onConfirm: () => {
                      setShowModal(false);
                      resetForm();
                      setConfirmData(null);
                    },
                  });
                } else {
                  setShowModal(false);
                  resetForm();
                }
              }}
              className={`rounded-xl font-medium border border-color text-secondary hover:bg-hover transition active:scale-[0.98] ${
                isMobile ? "w-full py-3 text-sm" : "px-4 py-2.5 text-sm"
              }`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              className={`rounded-xl font-semibold text-white shadow-md transition active:scale-[0.98] ${
                isMobile ? "w-full py-3 text-sm" : "px-5 py-2.5 text-sm"
              }`}
              style={{ backgroundColor: primaryColor }}
            >
              {form.id ? "Salvar alterações" : "Salvar evento"}
            </button>
          </div>
        }
      >
        <div className="space-y-3.5">
          {form.title && (
            <div className="p-3 bg-muted rounded-xl border border-color">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted mb-2">Preview</p>
              <div
                className="p-3 rounded-xl text-white text-sm shadow-sm"
                style={{ backgroundColor: form.color || DEFAULT_COLOR }}
              >
                <div className="flex items-center gap-2">
                  {form.type && EVENT_TYPES[form.type] && (() => {
                    const IconComponent = EVENT_TYPES[form.type].icon;
                    return <IconComponent size={14} />;
                  })()}
                  <span className="font-semibold truncate">{form.title}</span>
                  {form.timeStart && (
                    <span className="text-xs opacity-90 ml-auto tabular-nums">{form.timeStart}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <label className={`font-medium text-secondary mb-1.5 block ${isMobile ? "text-xs" : "text-sm"}`}>
              Título <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Ex: Visita ao apartamento"
              className={`surface-input w-full outline-none ${isMobile ? "px-3 py-2.5 text-[16px]" : "px-3.5 py-2.5 text-sm"}`}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={100}
              autoFocus
            />
            <div className="flex justify-end mt-1">
              <span className="text-[10px] text-muted">{form.title.length}/100</span>
            </div>
          </div>

          <div>
            <label className={`font-medium text-secondary mb-1.5 block ${isMobile ? "text-xs" : "text-sm"}`}>
              Tipo de evento
            </label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className={`surface-input w-full outline-none ${isMobile ? "px-3 py-2.5 text-[16px]" : "px-3.5 py-2.5 text-sm"}`}
            >
              {Object.entries(EVENT_TYPES).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={`font-medium text-secondary mb-1.5 block ${isMobile ? "text-xs" : "text-sm"}`}>
              Descrição
            </label>
            <textarea
              placeholder="Detalhes do evento (opcional)"
              className={`surface-input w-full outline-none resize-none ${isMobile ? "px-3 py-2.5 text-[16px]" : "px-3.5 py-2.5 text-sm"}`}
              rows={isMobile ? 2 : 3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              maxLength={500}
            />
          </div>

          <div>
            <label className={`font-medium text-secondary mb-1.5 block ${isMobile ? "text-xs" : "text-sm"}`}>
              Data <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              className={`surface-input w-full outline-none ${isMobile ? "px-3 py-2.5 text-[16px]" : "px-3.5 py-2.5 text-sm"}`}
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className={`font-medium text-secondary mb-1.5 block ${isMobile ? "text-xs" : "text-sm"}`}>Início</label>
              <input
                type="time"
                className={`surface-input w-full outline-none ${isMobile ? "px-3 py-2.5 text-[16px]" : "px-3.5 py-2.5 text-sm"}`}
                value={form.timeStart}
                onChange={(e) => setForm({ ...form, timeStart: e.target.value })}
              />
            </div>
            <div>
              <label className={`font-medium text-secondary mb-1.5 block ${isMobile ? "text-xs" : "text-sm"}`}>Término</label>
              <input
                type="time"
                className={`surface-input w-full outline-none ${isMobile ? "px-3 py-2.5 text-[16px]" : "px-3.5 py-2.5 text-sm"}`}
                value={form.timeEnd}
                onChange={(e) => setForm({ ...form, timeEnd: e.target.value })}
                min={form.timeStart}
              />
            </div>
          </div>

          <div>
            <label className={`font-medium text-secondary mb-1.5 block ${isMobile ? "text-xs" : "text-sm"}`}>Cliente</label>
            <select
              className={`surface-input w-full outline-none ${isMobile ? "px-3 py-2.5 text-[16px]" : "px-3.5 py-2.5 text-sm"}`}
              value={form.clientId}
              onChange={(e) => setForm({ ...form, clientId: e.target.value })}
            >
              <option value="">— Nenhum cliente —</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={`font-medium text-secondary mb-1.5 block ${isMobile ? "text-xs" : "text-sm"}`}>Imóvel</label>
            <select
              className={`surface-input w-full outline-none ${isMobile ? "px-3 py-2.5 text-[16px]" : "px-3.5 py-2.5 text-sm"}`}
              value={form.propertyId}
              onChange={(e) => setForm({ ...form, propertyId: e.target.value })}
            >
              <option value="">— Nenhum imóvel —</option>
              {properties.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.title || property.name || "Imóvel"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={`font-medium text-secondary mb-1.5 block ${isMobile ? "text-xs" : "text-sm"}`}>Cor do evento</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.color || DEFAULT_COLOR}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className={`border border-color rounded-lg cursor-pointer ${isMobile ? "h-10 w-12" : "h-10 w-14"}`}
              />
              <span className="text-xs text-muted">Escolha uma cor para o calendário</span>
            </div>
          </div>
        </div>
      </NativeSheet>

{/* 🏢 V3: MODAL DE CONFIRMAÇÃO (PORTAL ENTERPRISE) */}
      <ConfirmModal
        isOpen={!!confirmData}
        data={confirmData}
        onConfirm={() => {
          if (confirmData?.onConfirm) {
            confirmData.onConfirm();
          } else {
            setConfirmData(null);
          }
        }}
        onCancel={() => {
          if (confirmData?.onCancel) {
            confirmData.onCancel();
          } else {
            setConfirmData(null);
          }
        }}
        isMobile={isMobile}
      />
    </div>
  );
}